'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useProduct } from '@/screens/Product/hooks/useProduct'

import { useRegion } from '@/entities/regions'
import { useUser } from '@/entities/user'

import ErrorIcon from '@/shared/assets/icons/alert-circle.svg'
import SuccessIcon from '@/shared/assets/icons/circle-check.svg'
import { BASE_URL } from '@/shared/lib/const/env'
import { useDebounce } from '@/shared/lib/hooks/new/useDebounce'
import { emailSchema, normalizeEmail } from '@/shared/lib/validation/email'
import { formatPhoneMasked, phoneSchema } from '@/shared/lib/validation/phone'
import {
	AgreementBlock,
	Autocomplete,
	Button,
	Image,
	Input,
	Modal,
	RHFEmailInput,
	RHFPhoneInput,
	Select
} from '@/shared/ui'
import { Icon } from '@/shared/ui/icon'
import { Skeleton } from '@/shared/ui/Skeleton'

import { Price } from '../../../product-price-widget/PricesBlock/Price/Price'

import {
	FLPDeliveryItem,
	useGetCitiesQuery,
	useLazyGetDeliveryOptionsQuery,
	useLazyGetPaymentOptionsQuery,
	useSendRequestMutation
} from './api/flpApi'

import cl from './found-lower-price-form.module.scss'

type FLPFormProps = {
	isOpened: boolean
	setIsModalOpen: (state: boolean) => void
}

const flpFormSchema = z.object({
	FIO: z
		.string({ required_error: 'Заполните поле' })
		.min(1, 'Заполните поле')
		.regex(/^[^-][а-яА-Я]*\s?[а-яА-Я]*?$/, 'Некорректный формат'),
	EMAIL: emailSchema,
	PHONE: phoneSchema,
	LINK: z
		.string({ required_error: 'Заполните поле' })
		.min(1, 'Заполните поле')
		.url('Введите корректную ссылку (например: https://example.com)'),
	CITY: z.string({ required_error: 'Выберите город' }).min(1, 'Выберите город'),
	DELIVERY: z.string({ required_error: 'Выберите способ получения' }).min(1, 'Выберите способ получения'),
	PAYMENT: z.string({ required_error: 'Выберите способ оплаты' }).min(1, 'Выберите способ оплаты')
})

type FLPFormData = z.infer<typeof flpFormSchema>

type DeliveryMeta = {
	deliveryId: string
	deliveryType: string
	storeId?: string
}

const formMessage = (status: number): JSX.Element => {
	switch (status) {
		case 200:
		case 201:
			return (
				<div className={cl.form__success}>
					<div className={cl.form__text}>
						<div className={cl.form__icon}>
							<Icon icon={SuccessIcon} aria-label="icon" width={44} height={44} />
						</div>
						<h2>Заявка успешно отправлена</h2>
						<p>Наши операторы скоро позвонят вам для подтверждения</p>
					</div>
				</div>
			)
		default:
			return (
				<div className={cl.form__success}>
					<div className={cl.form__text}>
						<div className={cl.iconWrap}>
							<Icon icon={ErrorIcon} aria-label="icon" width={40} height={40} />
						</div>
						<h2>Произошла ошибка, попробуйте позже</h2>
					</div>
				</div>
			)
	}
}

const buildDeliveryOptions = (deliveryData: FLPDeliveryItem[] | undefined) => {
	const options: { label: string; value: string }[] = []
	const metaMap = new Map<string, DeliveryMeta>()

	if (!deliveryData) return { options, metaMap }

	deliveryData.forEach(item => {
		if (item.TYPE === 'shop' && item.SHOPS?.length) {
			item.SHOPS.forEach(shop => {
				const key = `shop_${shop.UF_B24_VALUE_ID}`
				options.push({ label: `${shop.UF_TITLE}`, value: key })
				metaMap.set(key, {
					deliveryId: String(item.ID),
					deliveryType: item.TYPE,
					storeId: shop.UF_B24_VALUE_ID
				})
			})
		} else {
			const key = `delivery_${item.ID}`
			options.push({ label: item.NAME, value: key })
			metaMap.set(key, { deliveryId: String(item.ID), deliveryType: item.TYPE })
		}
	})

	return { options, metaMap }
}

export const FoundLowerPriceForm = ({ isOpened, setIsModalOpen }: FLPFormProps) => {
	const { product } = useProduct()
	const { currentRegion } = useRegion()
	const { user, isLoading: userInfoIsLoading } = useUser()
	const pathname = usePathname()
	const searchParams = useSearchParams()

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { errors }
	} = useForm<FLPFormData>({
		mode: 'onBlur',
		resolver: zodResolver(flpFormSchema),
		defaultValues: {
			FIO: '',
			EMAIL: '',
			PHONE: '',
			LINK: '',
			CITY: '',
			DELIVERY: '',
			PAYMENT: ''
		}
	})

	const search = searchParams.toString()
	const pageUrl = `https://www.iport.ru${pathname}${search ? `?${search}` : ''}`

	const [formResponse, setFormResponse] = useState<JSX.Element>(null)
	const [citySearch, setCitySearch] = useState('')
	const [selectedCity, setSelectedCity] = useState<string | null>(null)
	const deliveryMetaMapRef = useRef<Map<string, DeliveryMeta>>(new Map())
	const selectedDelivery = watch('DELIVERY')

	const debouncedCitySearch = useDebounce(citySearch, 500)
	const cityQueryParam = debouncedCitySearch || currentRegion?.REGION_NAME || ''

	const { data: citiesData } = useGetCitiesQuery(cityQueryParam, {
		skip: !!selectedCity || !cityQueryParam || cityQueryParam.length < 3
	})

	const [triggerGetDeliveryOptions, { data: deliveryData }] = useLazyGetDeliveryOptionsQuery()
	const [triggerGetPaymentOptions, { data: paymentData, isFetching: isPaymentLoading }] =
		useLazyGetPaymentOptionsQuery()
	const [sendFLP, { isLoading: isSending }] = useSendRequestMutation()

	const cityOptions = useMemo(() => {
		if (!citiesData) return []
		return citiesData.map(city => ({ label: city.NAME, value: city.NAME }))
	}, [citiesData])

	const deliveryOptions = useMemo(() => {
		if (!deliveryData) return []
		const { options, metaMap } = buildDeliveryOptions(deliveryData)
		deliveryMetaMapRef.current = metaMap
		return options
	}, [deliveryData])

	const paymentOptions = useMemo(() => {
		if (!paymentData) return []
		return paymentData.map(item => ({ label: item.NAME, value: item.ID.toString() }))
	}, [paymentData])

	useEffect(() => {
		if (!user) return

		const userName = user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name
		if (userName) setValue('FIO', userName)
		if (user.user_phone) setValue('PHONE', formatPhoneMasked(user.user_phone))
		if (user.user_email && user.user_email !== '0') {
			setValue('EMAIL', normalizeEmail(user.user_email))
		}
	}, [setValue, user])

	const resetDownstream = (fromCity: boolean) => {
		if (fromCity) setValue('DELIVERY', '')
		setValue('PAYMENT', '')
	}

	const handleCityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCitySearch(e.target.value)
		if (selectedCity) {
			setSelectedCity(null)
			setValue('CITY', '')
			resetDownstream(true)
		}
	}

	const handleCitySelect = (value: string) => {
		const selectedOption = cityOptions.find(opt => opt.value === value)
		setCitySearch(selectedOption?.label || value)
		setSelectedCity(value)
		setValue('CITY', value, { shouldValidate: true })
		triggerGetDeliveryOptions(value)
		resetDownstream(true)
	}

	const handleDeliveryChange = (value: string) => {
		setValue('DELIVERY', value)
		setValue('PAYMENT', '')
		const meta = deliveryMetaMapRef.current.get(value)
		if (meta && selectedCity) {
			triggerGetPaymentOptions({
				city: selectedCity,
				delivery: meta.deliveryType
			})
		}
	}

	const onFinish = async (values: FLPFormData): Promise<void> => {
		try {
			const { FIO, EMAIL, PHONE, LINK, DELIVERY, PAYMENT } = values

			const deliveryMeta = deliveryMetaMapRef.current.get(DELIVERY)

			const payload = {
				FIO,
				EMAIL,
				PHONE,
				LINK,
				CITY: selectedCity || '',
				DELIVERY_TYPE: deliveryMeta?.deliveryId || '',
				STORE: deliveryMeta?.storeId || '',
				PAY_TYPE: PAYMENT,
				PRODUCT_CODE: product.KOD,
				PAGE: pageUrl,
				TYPE: 'BestPriceIport'
			}

			const response = await sendFLP(payload).unwrap()
			setFormResponse(formMessage(response.code))
		} catch (e) {
			console.error('Произошла ошибка:', e)
			setFormResponse(formMessage(0))
		}
	}

	const onClose = () => setIsModalOpen(false)

	return (
		<Modal isOpen={isOpened} onClose={onClose} className={cl.modal}>
			{!formResponse ? (
				<>
					<h2 className={cl.title}>Нашли дешевле? Снизим цену!</h2>
					<p className={cl.text}>Заполните заявку и получите выгодное предложение</p>
					<Link prefetch={false} href="/found-lower-price" className={cl.link} target="_blank">
						Узнать подробнее
					</Link>

					<div className={cl.product}>
						<Image src={BASE_URL + product.IMAGES[0]} width={80} height={80} alt="" className={cl.product__image} />
						<div className={cl.product__content}>
							<h3 className={cl.product__title}>{product.TITLE}</h3>
							<div className={cl.product__price}>
								<Price price={product.PRICE.VALUE} className={cl.mainPrice} />
								{product.OLD_PRICE?.VALUE && <Price className={cl.oldPrice} price={product.OLD_PRICE?.VALUE} isOld />}
							</div>
						</div>
					</div>

					<div>
						<form onSubmit={handleSubmit(onFinish)} className={cl.form}>
							<div className={clsx(cl.form__item, cl.form__item_fio)}>
								<Controller
									name="FIO"
									control={control}
									render={({ field }) =>
										userInfoIsLoading ? (
											<Skeleton height={48} borderRadius={8} />
										) : (
											<Input {...field} label="Имя" isError={!!errors.FIO} helperText={errors.FIO?.message} />
										)
									}
								/>
							</div>

							<div className={clsx(cl.form__item, cl.form__item_phone)}>
								<RHFPhoneInput name="PHONE" control={control} error={errors.PHONE} isLoading={userInfoIsLoading} />
							</div>

							<div className={clsx(cl.form__item, cl.form__item_email)}>
								<RHFEmailInput name="EMAIL" control={control} error={errors.EMAIL} isLoading={userInfoIsLoading} />
							</div>

							<div className={clsx(cl.form__item, cl.form__item_link)}>
								<Controller
									name="LINK"
									control={control}
									render={({ field }) => (
										<Input
											{...field}
											label="Ссылка на товар конкурентов"
											isError={!!errors.LINK}
											helperText={errors.LINK?.message}
										/>
									)}
								/>
							</div>

							<div className={clsx(cl.form__item, cl.form__item_city)}>
								<Controller
									name="CITY"
									control={control}
									render={() => (
										<Autocomplete
											value={citySearch}
											label="Город"
											options={cityOptions}
											onChange={handleCityInputChange}
											onSelectOption={handleCitySelect}
										/>
									)}
								/>
								{errors.CITY && <span className={cl.form__error}>{errors.CITY.message}</span>}
							</div>

							<div className={clsx(cl.form__item, cl.form__item_delivery)}>
								<Controller
									name="DELIVERY"
									control={control}
									render={({ field }) => (
										<Select
											value={field.value}
											placeholder="Способ получения"
											adaptive
											options={deliveryOptions}
											disabled={!selectedCity}
											error={!!errors.DELIVERY}
											onChange={handleDeliveryChange}
										/>
									)}
								/>
								{errors.DELIVERY && <span className={cl.form__error}>{errors.DELIVERY.message}</span>}
							</div>

							<div className={clsx(cl.form__item, cl.form__item_payment)}>
								<Controller
									name="PAYMENT"
									control={control}
									render={({ field }) => (
										<Select
											value={field.value}
											placeholder="Способ оплаты"
											adaptive
											options={paymentOptions}
											disabled={!selectedDelivery}
											loading={isPaymentLoading}
											error={!!errors.PAYMENT}
											onChange={value => field.onChange(value)}
										/>
									)}
								/>
								{errors.PAYMENT && <span className={cl.form__error}>{errors.PAYMENT.message}</span>}
							</div>

							<Button className={cl.form__submit} type="submit" isLoading={isSending} disabled={isSending}>
								Отправить заявку
							</Button>
							<div className={cl.form__agreement}>
								<AgreementBlock />
							</div>
						</form>
					</div>
				</>
			) : (
				formResponse
			)}
		</Modal>
	)
}
